import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IdAndFaceValidation, IdValidation } from 'idmission-web-sdk';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { IconCell } from '@/components/Icon/IconCell';
import { ErrorMessage } from '@/components/Idmission/ErrorMessage';
import { IdmButton } from '@/components/Idmission/IdmButton.styled';
import { IdmCard } from '@/components/Idmission/IdmCard.styled';
import {
  IdmClassNames,
  IdmStyles,
} from '@/components/Idmission/Idmission.styled';
import { RejectedMessage } from '@/components/Idmission/RejectedMessage';
import {
  Form,
  FullPageOverlayBackground,
} from '@/components/Layout/Layout.styled';
import { UnifiedPage } from '@/components/Layout/Page/UnifiedPage';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';
import { useIdmissionVerbiage } from '@/hooks/verification/useIdmissionVerbiage';
import { useIdmissionVerification } from '@/hooks/verification/useIdmissionVerification';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { PROGRESS_STEPS } from '@/utils/progressSteps';
import { renderHTMLText } from '@/utils/string';
import {
  checkUserEkycStatus,
  isExistingCustomer,
  isRemediationCustomer,
} from '@/utils/user';

export function IdmissionIDValidation() {
  const user = useAppSelector(selectLoggedInUser);
  const { isSelfieChecked, isLivenessVideoRecorded } =
    checkUserEkycStatus(user);
  const [isVerificationStarted, setIsVerificationStarted] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslate();
  const { verbiage } = useIdmissionVerbiage();

  const onCompleted = () => {
    if (isExistingCustomer(user) || isRemediationCustomer(user)) {
      navigate(ROUTE.MORE_INFORMATION);
    } else {
      navigate(ROUTE.BASIC_INFO);
    }
  };

  const {
    sessionId,
    isIDRejected,
    isSelfieRejected,
    isLivenessRejected,
    acknowledgeRejection,
    error,
    isProcessing,
    isCompleted,
    shouldValidateFace,
    idCaptureRequirement,
    startVerification,
    handleBeforeLivenessCheck,
    handleComplete,
    handleAcknowledgeRejection,
    resetVerification,
    handleOnRequestFailure,
  } = useIdmissionVerification({
    type: 'id',
    autoInit: false,
    isSelfieChecked,
    isLivenessVideoRecorded,
    onCompleted,
  });

  const handleStartVerification = useCallback(async () => {
    try {
      setIsVerificationStarted(true);
      setIsLoading(true);
      await startVerification();
    } catch (err) {
      console.error('Failed to start verification:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startVerification]);

  const handleOnExit = useCallback(() => {
    resetVerification();
    setIsVerificationStarted(false);
  }, [resetVerification]);

  const renderVerificationContent = () => {
    if (!sessionId || isProcessing || isCompleted) {
      return (
        <FullPageOverlayBackground bgColor={theme.palette.background.paper}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        </FullPageOverlayBackground>
      );
    }

    return (
      <FullPageOverlayBackground bgColor={theme.palette.background.paper}>
        <IdmStyles>
          {shouldValidateFace ? (
            <IdAndFaceValidation
              sessionId={sessionId}
              idCaptureRequirement={idCaptureRequirement}
              onBeforeLivenessCheck={handleBeforeLivenessCheck}
              onComplete={handleComplete}
              onExitCapture={handleOnExit}
              onExitAfterFailure={handleOnExit}
              onRequestFailure={handleOnRequestFailure}
              idCaptureAllowUploadingDocumentsFromStorage={false}
              classNames={IdmClassNames}
              verbiage={verbiage}
            />
          ) : (
            <IdValidation
              sessionId={sessionId}
              idCaptureRequirement={idCaptureRequirement}
              onComplete={handleComplete}
              onExitCapture={handleOnExit}
              onRequestFailure={handleOnRequestFailure}
              allowUploadingDocumentsFromStorage={false}
              classNames={IdmClassNames.idCapture}
              verbiage={verbiage.idCapture}
            />
          )}
        </IdmStyles>
      </FullPageOverlayBackground>
    );
  };

  if (
    (isIDRejected || isSelfieRejected || isLivenessRejected) &&
    !acknowledgeRejection
  ) {
    return (
      <RejectedMessage
        isIDRejected={isIDRejected}
        isSelfieRejected={isSelfieRejected}
        isLivenessRejected={isLivenessRejected}
        onAcknowledge={handleAcknowledgeRejection}
      />
    );
  }

  if (error) {
    return <ErrorMessage error={error} onAcknowledge={handleOnExit} />;
  }

  return (
    <UnifiedPage
      hasDarkBackground
      bgColor={(theme: Theme) => theme.palette.primaries[900]}
      progressProps={{
        ...PROGRESS_STEPS.livenessVerificationStep,
        activeColor: (theme: Theme) => theme.palette.primaries[400],
        inactiveColor: (theme: Theme) => theme.palette.grey[300],
      }}
      hasBackToLoginButton
    >
      <Typography variant="h1" mt={3}>
        {t('verification.idmission.title')}
      </Typography>
      <Form>
        <Typography variant="body2">
          {renderHTMLText(
            t(
              `verification.idmission.gettingStartedInstruction.${idCaptureRequirement}`,
            ),
          )}
        </Typography>
      </Form>

      {!isVerificationStarted ? (
        <IdmCard maxWidth={400}>
          <IconCell
            name="camera"
            color={(theme: Theme) => theme.palette.common.white}
            bgColor={(theme: Theme) => theme.palette.primaries[500]}
            size={80}
            iconSize={40}
          />
          <IdmButton
            onClick={handleStartVerification}
            variant="contained"
            disabled={isLoading}
            size="large"
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('verification.idmission.idCapture.captureWithCameraText')
            )}
          </IdmButton>
        </IdmCard>
      ) : (
        renderVerificationContent()
      )}
    </UnifiedPage>
  );
}
